import type { DiaDeProgresso } from '@titularis/api-client';

/**
 * Prepara a série do período para o gráfico.
 *
 * Três coisas acontecem aqui, e as três são necessárias para o desenho não
 * mentir.
 *
 * **Somar as pessoas.** A API devolve uma linha por bloco *e por autor*, porque
 * a tabela mostra quem fez. O gráfico é do time, então as linhas do mesmo bloco
 * viram uma.
 *
 * **Preencher o vazio.** Bloco sem trabalho não volta da API - não existe linha
 * para ele. Desenhar só o que voltou encostaria 24 de julho em 2 de agosto lado
 * a lado, e oito dias parados virariam uma barra ao lado da outra: a leitura
 * seria de trabalho contínuo, que é o oposto do que aconteceu. O tempo parado
 * precisa ocupar espaço e valer zero.
 *
 * **Fechar os dias.** A janela é preenchida em dias inteiros, e não do primeiro
 * ao último bloco com dado: as marcas do eixo caem no primeiro bloco de cada
 * dia (ver `densidadeDoRitmo.ts`), e dia começando no meio faria a data cair
 * onde o dia não começa.
 */

const FUSO = 'America/Sao_Paulo';

/*
 * O dia é o de Brasília, e não o do relógio de quem abre a tela.
 *
 * O agrupamento vem do banco já convertido para `America/Sao_Paulo`. Se aqui a
 * gente gerasse os dias em UTC, perto da meia-noite o rótulo gerado não bateria
 * com a chave devolvida, e o trabalho daquele dia apareceria como zero - com a
 * barra desenhada, o eixo certo e o número errado. Falha silenciosa.
 */
const emBrasilia = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSO,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export interface DiaDoGrafico {
  /**
   * A chave da coluna: `YYYY-MM-DDTHH`.
   *
   * Dia e hora juntos, porque é isto que a coluna é. Separados, duas colunas do
   * mesmo dia teriam a mesma chave e o Recharts empilharia as duas na primeira.
   */
  inicio: string;
  /** `YYYY-MM-DD`, como vem da API. */
  dia: string;
  /** Hora em que o bloco começa. Zero quando a coluna é o dia inteiro. */
  hora: number;
  versoes: number;
  /** Peças redigidas por uma pessoa. */
  pecas: number;
  /** Peças da máquina que uma pessoa assinou. Empilha com a de cima. */
  pecasValidadas: number;
  fechadas: number;
}

/**
 * Anda no calendário, para frente ou para trás.
 *
 * Ao meio-dia UTC de propósito: somar dias sobre a meia-noite tropeça em
 * qualquer mudança de fuso, e no meio do dia sobra folga dos dois lados.
 */
export function somarDias(dia: string, dias: number): string {
  const cursor = new Date(`${dia}T12:00:00Z`);
  cursor.setUTCDate(cursor.getUTCDate() + dias);
  return cursor.toISOString().slice(0, 10);
}

/** O dia de hoje em Brasília, no mesmo formato em que a série vem. */
export const diaDeHoje = (agora: Date = new Date()): string => emBrasilia.format(agora);

const diaAnterior = (dia: string) => somarDias(dia, -1);

const doisDigitos = (numero: number) => String(numero).padStart(2, '0');

const chaveDoBloco = (dia: string, hora: number) => `${dia}T${doisDigitos(hora)}`;

const vazio = (dia: string, hora: number): DiaDoGrafico => ({
  inicio: chaveDoBloco(dia, hora),
  dia,
  hora,
  versoes: 0,
  pecas: 0,
  pecasValidadas: 0,
  fechadas: 0,
});

export function serieDoRitmo(
  dados: DiaDeProgresso[],
  dias: number,
  bloco = 24,
  agora: Date = new Date(),
): DiaDoGrafico[] {
  const porBloco = new Map<string, DiaDoGrafico>();

  for (const linha of dados) {
    /*
     * A hora volta a cair no bloco desenhado, e não é remendo: a consulta pede
     * sempre o bloco mais fino que alguma tela usa, e é aqui que o celular
     * recolhe os três blocos do dia numa coluna só. Sem isto, a linha cairia
     * fora de toda coluna e sumiria em silêncio.
     */
    const hora = Math.floor(linha.hora / bloco) * bloco;
    const chave = chaveDoBloco(linha.dia, hora);

    const acumulado = porBloco.get(chave) ?? vazio(linha.dia, hora);
    acumulado.versoes += linha.versoes;
    acumulado.pecas += linha.pecas;
    acumulado.pecasValidadas += linha.pecasValidadas;
    acumulado.fechadas += linha.fechadas;
    porBloco.set(chave, acumulado);
  }

  /*
   * A janela do gráfico é a união do calendário pedido com o que veio.
   *
   * Só o calendário não bastaria: a API corta por instante (`agora - dias`), e
   * não por virada de dia, então a linha mais antiga cai no dia anterior ao
   * primeiro que a gente geraria. Filtrar por data seria descartar trabalho de
   * verdade sem avisar - e ele continuaria aparecendo na tabela logo abaixo,
   * com o gráfico e a tabela discordando na mesma tela.
   */
  const janela = new Set(dados.map((linha) => linha.dia));
  let cursor = emBrasilia.format(agora);
  for (let volta = 0; volta < dias; volta += 1) {
    janela.add(cursor);
    cursor = diaAnterior(cursor);
  }

  // Do mais antigo para o mais recente: a API entrega ao contrário, para a
  // tabela, e tempo em gráfico anda para a direita.
  const colunas: DiaDoGrafico[] = [];
  for (const dia of [...janela].sort()) {
    for (let hora = 0; hora < 24; hora += bloco) {
      colunas.push(porBloco.get(chaveDoBloco(dia, hora)) ?? vazio(dia, hora));
    }
  }
  return colunas;
}

/**
 * Dobra os blocos de volta em dias, somando o que cada pessoa fez.
 *
 * A tabela abaixo do gráfico é por dia e por pessoa, e continua sendo: uma
 * linha por bloco a deixaria três vezes mais longa para dizer o mesmo. Ela
 * dobra o que o gráfico já buscou em vez de pedir a série de novo por dia - a
 * segunda ida ao servidor traria os mesmos números com a chance de discordarem
 * dos primeiros, que é o defeito que não se percebe.
 */
export function linhasPorDia(dados: DiaDeProgresso[]): DiaDeProgresso[] {
  const porDia = new Map<string, DiaDeProgresso>();

  for (const linha of dados) {
    const chave = `${linha.dia}-${linha.autorId ?? 'sem-autor'}`;
    const acumulado = porDia.get(chave);
    if (!acumulado) {
      // A hora do dia dobrado é zero, que é o que a API devolve no bloco de dia.
      porDia.set(chave, { ...linha, hora: 0 });
      continue;
    }
    acumulado.versoes += linha.versoes;
    acumulado.pecas += linha.pecas;
    acumulado.pecasValidadas += linha.pecasValidadas;
    acumulado.fechadas += linha.fechadas;
  }

  // A ordem é a da API, que já vem do mais recente para o mais antigo.
  return [...porDia.values()];
}

/**
 * `2026-08-09` -> `09/08`. O ano não cabe e não muda dentro da janela.
 *
 * Aceita a chave da coluna inteira (`2026-08-09T08`) porque é ela que o eixo
 * carrega: a marca cai sempre no primeiro bloco do dia, e o que ela anuncia é
 * o dia.
 */
export function rotuloDoDia(dia: string): string {
  const [, mes, resto] = dia.split('-');
  return `${resto?.slice(0, 2)}/${mes}`;
}

/**
 * O rótulo do bloco, para o balão: `09/08 · 08h-16h`.
 *
 * Com a coluna valendo o dia inteiro, o intervalo some: "09/08 · 00h-24h" seria
 * ruído para dizer o que a data já diz.
 */
export function rotuloDoBloco(coluna: DiaDoGrafico, bloco: number): string {
  const dia = rotuloDoDia(coluna.dia);
  if (bloco >= 24) return dia;
  return `${dia} · ${doisDigitos(coluna.hora)}h-${doisDigitos(coluna.hora + bloco)}h`;
}
